import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Typography
} from '@mui/material';
import { Formik } from 'formik';
import useSessionTimeRangeService from '../../api/services/SessionTimeRangeService';
import { toast } from 'react-hot-toast';
import * as Yup from 'yup';

const SessionTimeRangeSchema = Yup.object().shape({
  sessionTime: Yup.string()
    .required('Session time is required')
    .min(1, 'Session time must be at least 1 character'),
  timeDescription: Yup.string()
    .optional(),
});

const EditSessionTimeRangeDialog = ({ sessionTimeRangeId, open, onClose, onSuccess }) => {
  const [initialData, setInitialData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getSessionTimeRangeById, updateSessionTimeRange } = useSessionTimeRangeService();

  useEffect(() => {
    const fetchSessionTimeRangeData = async () => {
      if (!sessionTimeRangeId) return;

      setIsLoading(true);
      try {
        const sessionTimeRange = await getSessionTimeRangeById(sessionTimeRangeId);

        setInitialData({
          id: sessionTimeRange.id,
          sessionTime: sessionTimeRange.sessionTime || '',
          timeDescription: sessionTimeRange.timeDescription || '',
          status: sessionTimeRange.status || 'ACTIVE',
        });
      } catch (err) {
        console.error('Failed to load session time range data:', err);
        toast.error('Failed to load session time range details');
      } finally {
        setIsLoading(false);
      }
    };

    if (open && sessionTimeRangeId) {
      fetchSessionTimeRangeData();
    } else {
      setInitialData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sessionTimeRangeId]);

  const handleSave = async (values, { setSubmitting }) => {
    setIsSubmitting(true);
    try {
      const payload = {
        id: values.id,
        sessionTime: values.sessionTime,
        timeDescription: values.timeDescription || null,
        status: values.status,
      };

      await updateSessionTimeRange(payload);
      toast.success("Session time range updated successfully!");
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to update session time range:', error);
      toast.error(error.response?.data?.message || 'Failed to update session time range');
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>EDIT SESSION TIME RANGE</DialogTitle>
      <DialogContent sx={{ position: 'relative' }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : initialData ? (
          <Formik
            enableReinitialize
            initialValues={initialData}
            validationSchema={SessionTimeRangeSchema}
            onSubmit={handleSave}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting: formikSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                {isSubmitting && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
                <Grid container spacing={2} paddingTop={1}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant="filled"
                      label="Session Time *"
                      name="sessionTime"
                      placeholder="e.g., 08:00 - 09:00"
                      value={values.sessionTime}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!touched.sessionTime && !!errors.sessionTime}
                      helperText={touched.sessionTime && errors.sessionTime}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      variant="filled"
                      label="Time Description (Optional)"
                      name="timeDescription"
                      multiline
                      rows={3}
                      placeholder="e.g., Morning Session"
                      value={values.timeDescription}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!touched.timeDescription && !!errors.timeDescription}
                      helperText={touched.timeDescription && errors.timeDescription}
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth variant="filled">
                      <InputLabel>Status</InputLabel>
                      <Select
                        name="status"
                        value={values.status}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={isSubmitting}
                      >
                        <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                        <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <DialogActions sx={{ mt: 2 }}>
                  <Button
                    onClick={onClose}
                    color=""
                    variant="contained"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    color="secondary"
                    variant="contained"
                    disabled={isSubmitting || formikSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                  >
                    {isSubmitting ? 'Saving...' : 'Save'}
                  </Button>
                </DialogActions>
              </form>
            )}
          </Formik>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography>No session time range data available</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditSessionTimeRangeDialog;

